import { computed, reactive, ref, watch } from 'vue';
import { axios } from '@/js/utils/axios.ts';
import { route, toast } from '@/js/utils.js';
import type { BudgetEntry } from '@/js/types/budgetEntry.js';
import { Eventbus } from '@/js/utils/eventbus.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const getDefaultForm = () => ({
    name: '',
    category_id: null,
    supplier_id: null,
    fixed_cost: '0',
    budgeted_cost: '0',
    department_id: null,
});

type CreateBudgetEntryForm = {
    name: string;
    category_id: number | null;
    supplier_id: number | null;
    fixed_cost: string;
    budgeted_cost: string;
    department_id?: number | null;
};

export const useCreateBudgetEntry = () => {
    const form = reactive<CreateBudgetEntryForm>(getDefaultForm());
    const isSubmitting = ref(false);

    const resetForm = () => {
        Object.assign(form, getDefaultForm());
    };

    watch(
        () => form.department_id,
        () => {
            form.category_id = null;
        }
    );

    const isValid = computed(() => {
        return (
            form.category_id &&
            form.name &&
            form.supplier_id &&
            Number.isFinite(Number(form.fixed_cost)) &&
            Number.isFinite(Number(form.budgeted_cost)) &&
            form.department_id
        );
    });

    const submit = async ({ onCreated }: { onCreated?: (entry: BudgetEntry) => void } = {}) => {
        if (!isValid.value || isSubmitting.value) {
            return;
        }

        isSubmitting.value = true;

        try {
            const response = await axios.post(route('api.account.event.entries.create'), form).catch((error) => {
                capturePosthogEvent('budget_entry_creation_failed');
                throw error;
            });
            capturePosthogEvent('budget_entry_created');
            resetForm();
            Eventbus.$emit('reload:entries');
            onCreated?.(response.data);
            toast('Success', 'Budget entry created successfully', 'success');
        } finally {
            isSubmitting.value = false;
        }
    };

    return {
        submit,
        form,
        isValid,
        isSubmitting,
    };
};
